import React, { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, Box, Typography, Table, TableBody, TableRow, TableCell, Stack, Button, Divider, Tooltip } from '@mui/material'
import { Order, OrderStatus, OrderType, productToJSON, TransactionType } from '../../proto/generated/order'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { toLocaleStringIn } from '../../util/general'
import { exchangeToJSON } from '../../proto/generated/common'
import useStyles from './utils/dialogStyles'
import { Broker } from '../../proto/generated/trading-bridge'

interface OrderInfoDialogProps {
    open: boolean
    onClose: () => void
    onViewTrades: () => void
    onViewHistory: () => void
    order: Order | null
    broker?: Broker
}

const orderTypeToString = (type: OrderType) => {
    switch (type) {
        case OrderType.MARKET_ORDER:
            return 'MARKET'
        case OrderType.LIMIT_ORDER:
            return 'LIMIT'
        case OrderType.STOPLOSS_ORDER:
            return 'SL'
        case OrderType.STOPLOSS_MARKET_ORDER:
            return 'SL-M'
        default:
            return 'UNKNOWN'
    }
}

const labelCellStyle = {
    border: 'none',
    py: 0.75,
    px: 0,
    color: 'text.secondary',
    fontSize: '13px',
    fontWeight: 400,
    width: '45%',
    lineHeight: 1.4,
    textAlign: 'left' as const
}

const valueCellStyle = {
    border: 'none',
    py: 0.75,
    px: 0,
    fontSize: '13px',
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.4,
    textAlign: 'right' as const
}

const getOrderStatusText = (status: OrderStatus | undefined) => {
    if (status == null) return 'UNKNOWN'

    switch (status) {
        case 0:
            return 'UNKNOWN'
        case 2:
            return 'PENDING'
        case 4:
            return 'FAILED'
        case 6:
            return 'OPEN'
        case 8:
            return 'REJECTED'
        case 10:
            return 'PARTIAL'
        case 12:
            return 'COMPLETE'
        case 14:
            return 'CANCELLED'
        case -1:
            return 'UNKNOWN'
    }
}

const OrderInfoDialog: React.FC<OrderInfoDialogProps> = ({ open, onClose, onViewTrades, onViewHistory, order, broker }) => {
    const { classes } = useStyles()
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | null>(null)
    useEffect(() => {
        if (!order?.security) { setSecurity(null); return }
        const key = getSecurityKey(order.security.exchange ?? 0, order.security.code ?? '')
        getSecurityAsync(key).then(setSecurity)
    }, [order?.security])

    const isOrderHistorySupported = useMemo(() => {
        return broker?.orderHistorySupported ?? false
    }, [broker?.orderHistorySupported])

    if (!order) return null

    const formatTimestamp = (ts: number | string) => {
        if (!ts || ts === 0) return '-'

        let date: Date

        if (typeof ts === 'string') {
            if (ts.includes('-') && ts.includes(' ')) {
                date = new Date(ts)
            } else {
                const num = parseInt(ts)
                const adjusted = num < 1e10 ? num * 1000 : num
                date = new Date(adjusted)
            }
        } else {
            const adjusted = ts < 1e10 ? ts * 1000 : ts
            date = new Date(adjusted)
        }

        if (isNaN(date.getTime())) return '-'

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        })
    }

    const formatPrice = (price?: number) => (!price ? '0' : toLocaleStringIn(price / (security?.priceMultiplier ?? 100)))

    const leftRows = [
        ['Quantity', `${order.filledQuantity || 0} / ${order.quantity || 0}`],
        ['Price', formatPrice(order.price)],
        ['Avg. price', formatPrice(order.averageTradedPrice)],
        ['Trigger price', formatPrice(order.triggerPrice)],
        ['Order type', orderTypeToString(order.orderType)],
        ['Product', productToJSON(order.product)],
        ['Validity', 'DAY']
    ]

    const rightRows = [
        ['Order ID', order.orderId || '-'],
        ['Exchange order ID', order.brokerOrderId || '-'],
        ['Time', formatTimestamp(order.created || 0)],
        ['Exchange time', formatTimestamp(order.exchangeTimestamp || 0)],
        ['Placed by', order.clientOrderId?.substring(0, 6) || 'USER']
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: classes.dialog,
                sx: {
                    minHeight: 300,
                    maxHeight: 500,
                    maxWidth: 700
                }
            }}
        >
            <Box className={classes.header}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            className={`${classes.transactionBadge} ${order.transactionType === TransactionType.BUY ? 'green-text' : 'red-text'}`}
                            sx={{
                                backgroundColor: order.transactionType === TransactionType.BUY ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                            }}
                        >
                            {order.transactionType === TransactionType.BUY ? 'BUY' : 'SELL'}
                        </Box>
                        <Typography variant="h6">{security?.symbol || order.security?.code}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {order.security?.exchange !== undefined ? exchangeToJSON(order.security.exchange) : ''}
                        </Typography>
                    </Stack>
                    <Box
                        sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#9E9E9E'
                        }}
                    >
                        {getOrderStatusText(order.status)}
                    </Box>
                </Stack>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', gap: 3, p: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Table size="small">
                            <TableBody>
                                {leftRows.map(([label, value]) => (
                                    <TableRow key={label}>
                                        <TableCell sx={labelCellStyle}>{label}</TableCell>
                                        <TableCell sx={valueCellStyle}>{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box sx={{ flex: 1, pl: 2 }}>
                        <Table size="small">
                            <TableBody>
                                {rightRows.map(([label, value]) => (
                                    <TableRow key={label}>
                                        <TableCell sx={labelCellStyle}>{label}</TableCell>
                                        <TableCell sx={valueCellStyle}>{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>

                <Box className={classes.footer} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 'auto', py: 1.5 }}>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title={!isOrderHistorySupported ? 'Trade history not supported by this broker' : ''} arrow placement={'top'} disableHoverListener={isOrderHistorySupported}>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onViewTrades}
                                    disabled={!isOrderHistorySupported}
                                    className={classes.button}
                                    sx={{ minWidth: 100 }}
                                >
                                    View trades
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title={!isOrderHistorySupported ? 'Order history not supported by this broker' : ''} arrow placement={'top'} disableHoverListener={isOrderHistorySupported}>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onViewHistory}
                                    disabled={!isOrderHistorySupported}
                                    className={classes.button}
                                    sx={{ minWidth: 100 }}
                                >
                                    View history
                                </Button>
                            </span>
                        </Tooltip>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                            className={classes.button}
                            sx={{ minWidth: 80 }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default OrderInfoDialog
